<template>
    <div>
      <v-row>
        <v-overlay :value="isProcessing">
          <v-progress-circular
            indeterminate
            color="primary"
            :size="70"
            :width="7"
          ></v-progress-circular>
        </v-overlay>
        <v-dialog v-model="dialog" width="500px">
          <v-card class="confirmation-modal">
            <v-img
              class="modal-icon"
              width="84"
              :src="thumbsUpImage"
            />
            <h2 class="mt-8 mb-8">
              Thank you, your individual dog registration request has been submitted. You will be notified of approval.
            </h2>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogError" width="500px">
          <v-card class="confirmation-modal">
            <h2 class="mt-8 mb-8">
              {{ errorMessage }}
            </h2>
          </v-card>
        </v-dialog>
        <v-col md="5" class="pb-0 hidden-sm-and-down">
          <v-img
            height="100vh"
            :src="registerDogImage"
            class="sticky-image"
          />
        </v-col>
        <OwnerInformation
          v-if="currentPage === 1"
          :changePage="changePage"
          :registerMember="registerMember"
          :renewMember="renewMember"
          :dogOwners="dogOwners"
          :updateSelectedCoownersFn="updateSelectedCoowners"
          :searchCoownersFn="dogRegistrationService.searchCoOwner"
          :signature="signature"
          :updateSignatureFn="updateSignature"
        />
        <DogInformation
          v-if="currentPage === 2"
          :changePage="changePage"
          :dogInfo="registerDogIndividualDTO"
          :puppyInfo="puppyInformation"
          :dogsLookups="dogsLookups"
          :setDogRegistrationFee="setDogRegistrationFee"
          :price="price"
          :priceInfo="priceInfo"
          :feesLoaded="feesLoaded"
        />
        <SummaryPayment
          v-if="currentPage === 3"
          :changePage="changePage"
          :submit="submit"
          :price="price"
          :priceInfo="priceInfo"
          :feesLoaded="feesLoaded"
        />
      </v-row>
    </div>
  </template>
  
  <script lang="ts">
  import Vue from "vue";
  import OwnerInformation from "./Steps/OwnerInformation.vue";
  import DogInformation from "./Steps/DogInformation.vue";
  import SummaryPayment from "./Steps/SummaryPayment.vue";
  import dogRegistrationService from "@/services/dogRegistrationService";
  import workflowService from "@/services/portal/workflowService";
  import { getErrorMessage } from "@/services/errorHandler";
  import { UserInfo } from "@/types/User";
  import { CoOwnerResult, DogRegistrationIndividualRequest, CoownerDTO } from "@/types/portal/Registration";
  import AuthorizeNetResponseData from "@/types/AuthorizeNetResponseData";
  import lookupsService from "@/services/lookupsService";
  const thumbsUpImage = require('@/assets/thumbs-up-duotone.svg');
  const registerDogImage = require('@/assets/register-dog.png');
  const defaultImage = require('@/assets/photouploadblue.png');

  export default Vue.extend({
    name: "RegisterDogIndividual",
    components: {
      OwnerInformation,
      DogInformation,
      SummaryPayment,
    },
    props: {
      puppyId: { type: Number },
    },
    mounted: function() {
      const userInfo = this.$store.state.userInfo;
      if (!!userInfo) this.initialize();
    },
    data: function () {
      return {
        defaultImage,
        thumbsUpImage,
        registerDogImage,
        isProcessing: false,
        currentPage: 1,
        dialog: false,
        dialogError: false,
        errorMessage: "",
        registerMember: false,
        renewMember: false,
        signature: '' as string,
        registerDogIndividualDTO: {
          //needs to be provided during registration
          puppyId: this.$props.puppyId,
          officialName: '',
          callName: '',
          trimColor: '',
          leftEyeColor: '',
          rightEyeColor: '',
          tattooChipNumber: '',
          notForBreeding: false,
        },
        puppyInformation: {
          litterOwners: [] as any[],
        },
        dogOwners: [] as any[],
        price: null as null | number,
        responseStatus: {},
        dogsLookups: {},
        priceInfo: [],
        feesLoaded: false,
        initialized: false,
      }
    },
    methods: {
      initialize: async function() {
        this.isProcessing = true;
        const {
          hasNeverBeenMember,
          isActiveMember
        } = this.userInfo;
        if (hasNeverBeenMember) this.registerMember = true;
        else if (!isActiveMember) this.renewMember = true;          
        
        try {
          await Promise.all([
            lookupsService.getDogsAttributes().then((res) => {
              this.dogsLookups = res;
            }),
            dogRegistrationService
              .getPuppyRegistrationInfoById(this.$props.puppyId)
              .then((res: any) => {
                this.puppyInformation = res;
                this.dogOwners = res.litterOwners;
              }),
            workflowService.getDogRegistrationIndividualFees().then((res) => {
              this.priceInfo = res;
              this.feesLoaded = true;
            }),
          ]);
          this.initialized = true;
        } catch (error) {
          this.$handleError(error);
        } finally {
          this.isProcessing = false;
        }
      },
      setDogRegistrationFee: async function(fee: number) {
        this.price = fee;
      },
      updateSignature: function(signature: string) {
        this.signature = signature;
      },
      changePage(page: number) {
        if (page > 1 && !this.initialized) return;
        this.currentPage = page;
      },
      updateSelectedCoowners(coowners: any){
        this.dogOwners = coowners;
        if(this.dogOwners.length > 0 && !this.dogOwners.some(own => own.isPrimary)){
          this.dogOwners[0].isPrimary = true;
        }
      },
      submit: async function(paymentInfo: AuthorizeNetResponseData) {
        this.isProcessing = true;
        this.registerDogIndividualDTO.officialName = this.registerDogIndividualDTO.officialName.trim();

        let ownersInfo = [] as CoownerDTO[];
        this.dogOwners.forEach(co => {
          ownersInfo.push({personId: co.personId, isPrimary: co.isPrimary});
        })

        try {
          this.responseStatus = await dogRegistrationService.registerDogIndividual({
            paymentInfo,
            fee: this.price!,
            signatureText: this.signature,
            owners: ownersInfo,
            registerDogIndividualDTO: this.registerDogIndividualDTO
          });
        } catch (error) {
          this.responseStatus = error as object;
        } finally {
          this.isProcessing = false;
        }
      },
    },
    computed: {
      userInfo(): UserInfo {
        return this.$store.state.userInfo;
      },
      dogRegistrationService() {
        return dogRegistrationService;
      },
    },
    watch: {
      userInfo: function (newValue) {
        if (!!newValue) this.initialize();
      },
      responseStatus: function (newValue) {
        if (newValue?.success) {
          this.dialog = true;
        } else {
          this.dialogError = true;
          this.errorMessage = getErrorMessage(newValue);
        }
      },
      dialog: function (newValue) {
        if (!newValue) {
          this.isProcessing = false;
          this.$store.dispatch("loadPersonTasks");
          this.$navigate("/");
        }
      },
      dialogError: function (newValue) {
        if (!newValue && (this.responseStatus as any)?.data?.paymentIssue) {
          this.isProcessing = false;
          this.$store.dispatch("loadPersonTasks");
          this.$navigate("/");
        } else if (!newValue) this.isProcessing = false;
      }
    }
  });
  </script>
